import { ChangeDetectorRef, Component, forwardRef, OnInit } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR,
  ValidationErrors, Validator, Validators
} from '@angular/forms';
import { map, takeUntil } from 'rxjs/operators';

import { FormGroupConfig } from 'core/form-config-type';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import * as CustomValidators from 'core/validators';
import { PhoneNumberType } from 'generated/graphql';
import { Store } from '@ngrx/store';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { getPhonePlaceholder } from 'core/geography';

export interface PhoneFormModel {
  // Model of form value
  numberType: any;
  number?: string | null;
  ext?: string | null;
  verified: boolean;
  subscribed: boolean;
}

@Component({
  selector: 'app-phone-form',
templateUrl: './phone-form.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneFormComponent),
      multi: true
    }
  ]
})
export class PhoneFormComponent
  extends WithDisposableSubject()
  implements ControlValueAccessor, Validator, OnInit {

  defaultCountryCode?: string;
  // This gets wiped out pretty quickly by the input
  // we use this to set the flag icon
  countryCode = '';
  flagIcon = 'flag-icon-us';

  get placeholder(): string {
    return getPhonePlaceholder(this.defaultCountryCode);
  }

  formGroup: UntypedFormGroup;

  _onTouched = () => { };
  _onChangeCallback = () => { };

  constructor(
    private _store: Store,
    private _fb: UntypedFormBuilder,
    private _cd: ChangeDetectorRef
  ) {
    super();
    const config: FormGroupConfig<PhoneFormModel> = {
      numberType: [PhoneNumberType.Cell, [Validators.required]],
      number: [null, [Validators.required, CustomValidators.e164PhoneValidator]],
      ext: [null, [CustomValidators.digitsOnly]],
      verified: [false],
      subscribed: [false]
    };

    this.formGroup = this._fb.group(config);
  }


  get value(): PhoneFormModel {
    return this.formGroup.value;
  }

  ngOnInit() {
    this._store.select(selectCurrentCampaign).pipe(
      takeUntil(this.disposer)
    ).subscribe(campaign => {
      if (campaign) {
        this.defaultCountryCode = campaign.defaultCountry;
      }
    });

    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer)
    ).subscribe(
      () => this._handleDataChange()
    );
  }

  updateFlagIcon(cc: string) {
    this.countryCode = cc;
    this.flagIcon = 'flag-icon-' + cc.toLowerCase();
    this._cd.markForCheck();
  }

  /** Control Value Accessor Implementmenation */
  writeValue(obj: PhoneFormModel): void {
    if (obj) {
      this.formGroup.patchValue({
        ...obj
      });
    }
  }

  registerOnChange(fn: any): void {
    this._onChangeCallback = () => fn(this.value);
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }

  /** Validator Implementation */
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.formGroup.valid) {
      return null;
    }
    return { phone: true };
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.valueChanges.pipe(
      takeUntil(this.disposer),
      map(_ => this.value)
    ).subscribe(fn);
  }

  private _handleDataChange(): void {
    this._cd.markForCheck();
    if (this._onChangeCallback) {
      this._onChangeCallback();
    }
  }
}
